/**
 * Modals and toasts.
 *
 * The prototype drove these imperatively — openModal(html), toast(msg) — from
 * anywhere in the page. Keeping that ergonomics matters, because a dozen screens
 * open a dialog from a table row, so this exposes the same imperative calls
 * through a context rather than making every page own dialog state.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Icon } from './icons.jsx';

const OverlayContext = createContext(null);

let seq = 0;

export function OverlayProvider({ children }) {
  const [modal, setModal] = useState(null);
  const [toasts, setToasts] = useState([]);

  const closeModal = useCallback(() => setModal(null), []);

  /** openModal({ title, subtitle, size, icon, body, footer }) — body may be a
   *  render function receiving { close } so dialogs can dismiss themselves. */
  const openModal = useCallback((config) => setModal({ id: ++seq, ...config }), []);

  const toast = useCallback((message, tone) => {
    const id = ++seq;
    setToasts((t) => [...t, { id, message, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 2800);
  }, []);

  useEffect(() => {
    if (!modal) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal, closeModal]);

  const value = useMemo(() => ({ openModal, closeModal, toast }), [openModal, closeModal, toast]);

  return (
    <OverlayContext.Provider value={value}>
      {children}

      {modal && (
        <div
          className="overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className={`modal ${modal.size ?? ''}`} role="dialog" aria-modal="true">
            {(modal.title || modal.icon) && (
              <div className="modal-h">
                {modal.icon && <div className="ai-orb"><Icon name={modal.icon} /></div>}
                <div>
                  {modal.title && <h3>{modal.title}</h3>}
                  {modal.subtitle && <div className="sub">{modal.subtitle}</div>}
                </div>
                <button type="button" className="icon-btn ml-auto" onClick={closeModal} aria-label="Close">
                  <Icon name="x" />
                </button>
              </div>
            )}
            <div className="modal-b">
              {typeof modal.body === 'function' ? modal.body({ close: closeModal }) : modal.body}
            </div>
            {modal.footer && (
              <div className="modal-f">
                {typeof modal.footer === 'function' ? modal.footer({ close: closeModal }) : modal.footer}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="toasts">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.tone ?? ''}`}>
            <Icon name={t.tone === 'crit' ? 'alert' : 'check'} />
            <span>{t.message}</span>
          </div>
        ))}
      </div>
    </OverlayContext.Provider>
  );
}

export function useOverlay() {
  const ctx = useContext(OverlayContext);
  if (!ctx) throw new Error('useOverlay must be used inside <OverlayProvider>');
  return ctx;
}
